/* eslint-disable jsx-a11y/media-has-caption */
import { DeviceIds, deviceType } from '@/core/environment/deviceType';
import { LimitsBadge } from '@/features/account/accountModal/limitsSection/LimitsBadge';
import { DownloadNoWatermarkButton } from '@/features/downloadMedia/downloadNoWatermarkButton/DownloadNoWatermarkButton';
import { ImageEnhanceResult } from '@/features/enhanceResult/imageEnhanceResult/ImageEnhanceResult';
import { AiToolbar } from '@/features/imageEditor/aiToolbar';
import { ImageNavigationToolbar } from '@/features/imageEditor/imageNavigationToolbar/ImageNavigationToolbar';
import { MediaUnreachable } from '@/features/imageEditor/mediaUnreachable/MediaUnreachable';
import mediaDisplayTracker from '@/features/mediaDisplayTracker/mediaDisplayTacker';
import Tutorial from '@/features/tutorial/Tutorial';
import { UploadNewButton } from '@/features/uploadMedia/UploadNewButton/UploadNewButton';
import { UploaderRoot } from '@/features/uploadMedia/UploaderRoot';
import { HCaptchaDisclosure } from '@/ui/HCaptchaDisclosure';
import { MenuActions } from '@/layout/menuActions';
import { useOrderedTutorials } from '@/layout/resultLayout/useOrderedTutorials';
import { TaskType } from '@/services/uploaderService';
import { tutorialState } from '@/stores/tutorialStore';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import Spinner from '@/ui/content/Spinner';
import { mediaShownInComparerState } from '@/stores/mediaStore';
import { useLocalization } from '@/services/localizationService';
import { webOnboardingBulkUploadProcessing } from '@/stores/webOnboardingStore';
import Header from '../Header';
import { PageLayout } from '../PageLayout';
import { Navigation } from '../navigation';
import { useResultLayout } from './useResultLayout';

export function ResultLayout() {
  const {
    uploaderId,
    media,
    isVideo,
    onUpload,
    isMediaReady,
    isMediaVisible,
    isImageEditorVisible,
    isImageSelectorToolbarVisible,
    shouldShowEnhancementsLeftBadge,
    enhancementsLeftText,
  } = useResultLayout();
  const isMobile = useMemo(() => deviceType() === DeviceIds.Mobile, []);
  const tutorial = useRecoilValue(tutorialState);
  const [isAIToolbarCTAActive, setIsAIToolbarCTAActive] =
    useState<boolean>(false);

  const onCtaActiveChange = (event: boolean) => {
    setIsAIToolbarCTAActive(event);
  };

  const {
    isAiSettingsDropdownOpen,
    newModelsTutorialId,
    saveSettingsTutorialId,
    setIsAiSettingsDropdownOpen,
  } = useOrderedTutorials({
    isVideo: media.mediaType === TaskType.Video,
  });

  const hasProcessedWebOnboardingBulkUpload = useRecoilValue(
    webOnboardingBulkUploadProcessing
  );
  const resetHasProcessedWebOnboardingBulkUpload = useResetRecoilState(
    webOnboardingBulkUploadProcessing
  );

  const selectedMedia = useRecoilValue(mediaShownInComparerState);
  useEffect(() => {
    if (selectedMedia.outputUrl) {
      resetHasProcessedWebOnboardingBulkUpload();
    }
  }, [resetHasProcessedWebOnboardingBulkUpload, selectedMedia.outputUrl]);

  const { t, data } = useLocalization();

  return (
    <PageLayout title="Enhanced Image" shouldAvoidIndexing>
      {!selectedMedia.outputUrl && hasProcessedWebOnboardingBulkUpload ? (
        <div className="flex w-full h-full items-center justify-center section-foreground relative">
          <div className="space-y-8 relative">
            <div className="space-y-3 text-center max-w-xs relative">
              <Spinner
                color="black"
                size="25"
                inline={false}
                className="!py-2"
              />
              <h3 className="text-black SB25">{t(data.result.redirecting)}</h3>
              <p className="M15 text-overlay-black-60">
                {t(data.result.redirectingSubtitle)}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <UploaderRoot
          enableDragAndDrop={false}
          id={uploaderId}
          onUpload={onUpload}
        >
          <div className="h-full flex flex-col justify-between">
            {tutorial.isTutorialActive && <Tutorial />}
            <Header className="section-middleground">
              {shouldShowEnhancementsLeftBadge && !isMobile && !isVideo && (
                <>
                  <LimitsBadge label={enhancementsLeftText} />
                  <Header.Divider className="hidden xl:block" />
                </>
              )}
              <MenuActions className="hidden xl:flex" />
              <Header.Divider className="hidden xl:block" />
              <DownloadNoWatermarkButton
                isAIToolbarCTAActive={isAIToolbarCTAActive}
              />
              <Navigation.Button className="xl:hidden" />
            </Header>
            <Navigation className="fixed z-10 inset-0 xl:hidden">
              <MenuActions showHamburgerInMDSize />
            </Navigation>

            {isMediaReady && (
              <div
                className="relative flex bg-black h-[100%] xl:px-12 md:px-8 sm:px-8"
                style={{ maxHeight: `calc(100% - 146px)` }}
              >
                {isImageEditorVisible && (
                  <AiToolbar
                    isAiSettingsDropdownOpen={isAiSettingsDropdownOpen}
                    newModelsTutorialId={newModelsTutorialId}
                    onCtaActiveChange={onCtaActiveChange}
                    saveSettingsTutorialId={saveSettingsTutorialId}
                    setIsAiSettingsDropdownOpen={setIsAiSettingsDropdownOpen}
                  />
                )}
                {!isMediaVisible ? (
                  <MediaUnreachable />
                ) : isVideo ? (
                  <video
                    width="100%"
                    height="100%"
                    preload="auto"
                    playsInline
                    autoPlay
                    controls
                    onLoadStart={() => {
                      mediaDisplayTracker.setInfo({
                        media_type: 'video',
                        from_id: 'result',
                        has_watermark: false, // will always be false for videos, since "watermarked" videos do not exist so far
                      });
                      mediaDisplayTracker.start();
                    }}
                    onLoadedData={() => {
                      mediaDisplayTracker.complete();
                    }}
                    onError={() => {
                      // unfortunately there isn't any valuable information in the error event
                      mediaDisplayTracker.fail(
                        new Error('The video failed to load.')
                      );
                    }}
                  >
                    <source src={media.outputUrl} type="video/mp4" />
                  </video>
                ) : (
                  <ImageEnhanceResult
                    isImageSelectorToolbarVisible={
                      isImageSelectorToolbarVisible
                    }
                  />
                )}
              </div>
            )}
            <footer
              className="container-full py-4 section-middleground items-center grid"
              style={{
                gridTemplate: `
                "left center  right" auto
                "note note   note" auto
                / 1fr  auto  1fr
              `,
              }}
            >
              {!isVideo && <ImageNavigationToolbar />}
              {!isVideo && (
                <div
                  className="flex justify-center"
                  style={{ gridArea: 'note' }}
                >
                  <HCaptchaDisclosure className="text-[10px] text-center whitespace-nowrap" />
                </div>
              )}

              <div
                className="flex justify-end items-center"
                style={{ gridArea: 'right' }}
              >
                <UploadNewButton uploaderId={uploaderId} />
              </div>
            </footer>
          </div>
        </UploaderRoot>
      )}
    </PageLayout>
  );
}
